import React from 'react';
import { Wallet, Sprout, Heart, BookOpen, Shield, Sun } from 'lucide-react';
import { coreTopics } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const iconMap = {
  Wallet,
  Sprout,
  Heart,
  BookOpen,
  Shield,
  Sun
};

const TopicsSection = () => {
  const { isDark } = useTheme();
  
  return (
    <section id="topics" className={`relative py-24 ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      {/* Background accent */}
      <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent to-transparent ${isDark ? 'via-stone-800' : 'via-stone-200'}`} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-amber-500" />
            <span className="text-amber-500 font-medium uppercase tracking-wider text-sm">Core Topics</span>
            <div className="h-px w-12 bg-amber-500" />
          </div>
          
          <h2 className={`text-4xl sm:text-5xl font-bold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
            Pillars of Sovereignty
          </h2>
          
          <p className={`text-lg ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
            Explore the key areas we cover on our journey to self-reliance and freedom.
          </p>
        </div>

        {/* Topics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreTopics.map((topic, index) => {
            const Icon = iconMap[topic.icon];
            return (
              <div
                key={topic.id}
                className={`group relative border rounded-2xl p-8 transition-all duration-300 ${
                  isDark 
                    ? 'bg-stone-900/50 hover:bg-stone-900 border-stone-800 hover:border-amber-500/30' 
                    : 'bg-white hover:bg-white border-stone-200 hover:border-amber-500/50 shadow-sm hover:shadow-lg'
                }`}
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl transition-colors ${
                    isDark 
                      ? 'bg-amber-500/10 group-hover:bg-amber-500/20' 
                      : 'bg-amber-100 group-hover:bg-amber-200'
                  }`}>
                    <Icon className={`w-7 h-7 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <p className={`text-sm font-medium ${isDark ? 'text-amber-500/70' : 'text-amber-600/80'}`}>{topic.subtitle}</p>
                  <h3 className={`text-xl font-bold group-hover:text-amber-500 transition-colors ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                    {topic.title}
                  </h3>
                  <p className={`leading-relaxed ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                    {topic.description}
                  </p>
                </div>

                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden rounded-tr-2xl">
                  <div className={`absolute top-0 right-0 w-32 h-32 transform rotate-45 translate-x-16 -translate-y-16 transition-colors ${
                    isDark 
                      ? 'bg-gradient-to-bl from-amber-500/5 to-transparent group-hover:from-amber-500/10' 
                      : 'bg-gradient-to-bl from-amber-500/10 to-transparent group-hover:from-amber-500/20'
                  }`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TopicsSection;
